// CommunicationsLog.js
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './CommunicationsLog.css';

// ── Email Templates Picker (shown when type = email) ─────────────────────────
function TemplatePicker({ onSelect }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/email-templates`)
      .then((r) => r.ok ? r.json() : [])
      .then(setTemplates)
      .catch(() => {});
  }, []);

  if (templates.length === 0) return null;

  return (
    <div className="template-picker-wrapper">
      <button
        type="button"
        className="template-picker-btn"
        onClick={() => setOpen((p) => !p)}
      >
        📋 {open ? 'Hide Templates' : 'Use Template'}
      </button>
      {open && (
        <div className="template-picker-dropdown">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              className="template-picker-item"
              onClick={() => { onSelect(t); setOpen(false); }}
            >
              <span className="tpi-name">{t.name}</span>
              <span className="tpi-subject">{t.subject}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const COMM_TYPES = [
  { value: 'call',    label: 'Phone Call',    icon: '📞', color: '#10b981' },
  { value: 'email',   label: 'Email',         icon: '📧', color: '#3b82f6' },
  { value: 'note',    label: 'Note',          icon: '📝', color: '#8b5cf6' },
  { value: 'meeting', label: 'Meeting',       icon: '🤝', color: '#f59e0b' },
  { value: 'tour',    label: 'Tour/Showing',  icon: '🏠', color: '#06b6d4' },
  { value: 'sms',     label: 'SMS/Text',      icon: '💬', color: '#64748b' },
];

const DIRECTIONS = [
  { value: 'outbound', label: 'Outbound' },
  { value: 'inbound',  label: 'Inbound'  },
];

const defaultForm = {
  type: 'call',
  direction: 'outbound',
  subject: '',
  body: '',
  duration_minutes: '',
  date: new Date().toISOString().split('T')[0],
  created_by: '',
};

function getTypeInfo(type) {
  return COMM_TYPES.find((t) => t.value === type) || COMM_TYPES[2];
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return dateStr; }
}

function formatDuration(mins) {
  if (!mins) return null;
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export default function CommunicationsLog({ applicantId, applicantName }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [error, setError] = useState('');

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/communications`);
      if (res.ok) {
        const data = await res.json();
        setEntries(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to load communications:', err);
    } finally {
      setLoading(false);
    }
  }, [applicantId]);

  useEffect(() => {
    if (applicantId) loadEntries();
  }, [applicantId, loadEntries]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.body.trim()) { setError('Please enter a description.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
      };
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/communications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        await loadEntries();
        setShowForm(false);
        setForm(defaultForm);
      } else {
        const err = await res.json();
        setError(err.error || 'Failed to save.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/communications/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setEntries((prev) => prev.filter((e) => e.id !== id));
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const filtered = filterType === 'all'
    ? entries
    : entries.filter((e) => e.type === filterType);

  const counts = COMM_TYPES.reduce((acc, t) => {
    acc[t.value] = entries.filter((e) => e.type === t.value).length;
    return acc;
  }, {});

  return (
    <div className="comms-log">
      {/* Header */}
      <div className="comms-log-header">
        <div className="comms-log-header-left">
          <h3 className="comms-log-title">Communications Log</h3>
          <span className="comms-log-count">{entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}</span>
        </div>
        <button
          className="comms-log-add-btn"
          onClick={() => { setShowForm((p) => !p); setError(''); }}
        >
          {showForm ? '✕ Cancel' : '+ Log Communication'}
        </button>
      </div>

      {/* Integration notice */}
      <div className="comms-integration-notice">
        <span className="comms-integration-icon">🔌</span>
        <span>
          Entries are logged manually. <strong>RingCentral</strong> and <strong>Microsoft Outlook</strong> integrations
          can auto-populate this log in a future update.
        </span>
      </div>

      {/* Log Form */}
      {showForm && (
        <form className="comms-form" onSubmit={handleSubmit}>
          <div className="comms-form-grid">
            {/* Type + Direction */}
            <div className="comms-form-row">
              <div className="comms-form-group">
                <label>Type *</label>
                <div className="type-selector">
                  {COMM_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      className={`type-chip ${form.type === t.value ? 'active' : ''}`}
                      style={form.type === t.value ? { background: t.color, borderColor: t.color } : {}}
                      onClick={() => setForm((p) => ({ ...p, type: t.value }))}
                    >
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="comms-form-row two-col">
              <div className="comms-form-group">
                <label>Direction</label>
                <select
                  value={form.direction}
                  onChange={(e) => setForm((p) => ({ ...p, direction: e.target.value }))}
                >
                  {DIRECTIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div className="comms-form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                  required
                />
              </div>
            </div>

            {form.type === 'email' && (
              <div className="comms-form-row">
                <TemplatePicker
                  onSelect={(t) => setForm((p) => ({ ...p, subject: t.subject, body: t.body }))}
                />
              </div>
            )}

            <div className="comms-form-row two-col">
              <div className="comms-form-group">
                <label>Subject / Summary</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                  placeholder="e.g. Follow-up on 2BR application"
                />
              </div>
              {form.type === 'call' && (
                <div className="comms-form-group">
                  <label>Duration (minutes)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.duration_minutes}
                    onChange={(e) => setForm((p) => ({ ...p, duration_minutes: e.target.value }))}
                    placeholder="e.g. 15"
                  />
                </div>
              )}
              {form.type !== 'call' && (
                <div className="comms-form-group">
                  <label>Agent / Logged By</label>
                  <input
                    type="text"
                    value={form.created_by}
                    onChange={(e) => setForm((p) => ({ ...p, created_by: e.target.value }))}
                    placeholder="Your name"
                  />
                </div>
              )}
            </div>

            {form.type === 'call' && (
              <div className="comms-form-row">
                <div className="comms-form-group">
                  <label>Agent / Logged By</label>
                  <input
                    type="text"
                    value={form.created_by}
                    onChange={(e) => setForm((p) => ({ ...p, created_by: e.target.value }))}
                    placeholder="Your name"
                  />
                </div>
              </div>
            )}

            <div className="comms-form-row">
              <div className="comms-form-group">
                <label>Notes / Description *</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                  placeholder={
                    form.type === 'call'
                      ? 'Summary of the call — what was discussed, next steps...'
                      : form.type === 'email'
                      ? 'Email summary or paste body here...'
                      : 'Enter your note...'
                  }
                  rows={4}
                  required
                />
              </div>
            </div>
          </div>

          {error && <div className="comms-form-error">{error}</div>}

          <div className="comms-form-actions">
            <button type="button" className="comms-btn-cancel" onClick={() => { setShowForm(false); setError(''); setForm(defaultForm); }}>
              Cancel
            </button>
            <button type="submit" className="comms-btn-save" disabled={saving}>
              {saving ? 'Saving…' : '✓ Save Entry'}
            </button>
          </div>
        </form>
      )}

      {/* Filter Tabs */}
      {entries.length > 0 && (
        <div className="comms-filter-tabs">
          <button
            className={`comms-filter-tab ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            All <span className="comms-filter-count">{entries.length}</span>
          </button>
          {COMM_TYPES.filter((t) => counts[t.value] > 0).map((t) => (
            <button
              key={t.value}
              className={`comms-filter-tab ${filterType === t.value ? 'active' : ''}`}
              onClick={() => setFilterType(t.value)}
            >
              {t.icon} {t.label} <span className="comms-filter-count">{counts[t.value]}</span>
            </button>
          ))}
        </div>
      )}

      {/* Entries */}
      <div className="comms-entries">
        {loading ? (
          <div className="comms-loading">Loading communications…</div>
        ) : filtered.length === 0 ? (
          <div className="comms-empty">
            {entries.length === 0
              ? 'No communications logged yet. Click "+ Log Communication" to add the first entry.'
              : `No ${filterType} entries found.`}
          </div>
        ) : (
          <div className="comms-timeline">
            {filtered.map((entry) => {
              const typeInfo = getTypeInfo(entry.type);
              return (
                <div key={entry.id} className="comms-entry">
                  {/* Left: icon + vertical line */}
                  <div className="comms-entry-left">
                    <div
                      className="comms-entry-icon"
                      style={{ background: typeInfo.color + '20', color: typeInfo.color, borderColor: typeInfo.color + '40' }}
                    >
                      {typeInfo.icon}
                    </div>
                    <div className="comms-entry-line" />
                  </div>

                  {/* Right: content */}
                  <div className="comms-entry-body">
                    <div className="comms-entry-header">
                      <div className="comms-entry-meta">
                        <span className="comms-entry-type" style={{ color: typeInfo.color }}>
                          {typeInfo.label}
                        </span>
                        <span className={`comms-direction-badge ${entry.direction}`}>
                          {entry.direction === 'inbound' ? '↙ Inbound' : '↗ Outbound'}
                        </span>
                        {entry.source && entry.source !== 'manual' && (
                          <span className="comms-source-badge">via {entry.source}</span>
                        )}
                        {entry.duration_minutes && (
                          <span className="comms-duration">⏱ {formatDuration(entry.duration_minutes)}</span>
                        )}
                      </div>
                      <div className="comms-entry-right-meta">
                        <span className="comms-entry-date">{formatDate(entry.date)}</span>
                        <button
                          className="comms-delete-btn"
                          title="Delete entry"
                          onClick={() => setDeleteConfirm(entry.id)}
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    {entry.subject && (
                      <div className="comms-entry-subject">{entry.subject}</div>
                    )}

                    {entry.body && (
                      <div className="comms-entry-note">{entry.body}</div>
                    )}

                    {entry.created_by && (
                      <div className="comms-entry-agent">Logged by {entry.created_by}</div>
                    )}

                    {/* Inline delete confirm */}
                    {deleteConfirm === entry.id && (
                      <div className="comms-delete-confirm">
                        <span>Delete this entry?</span>
                        <button className="comms-btn-confirm-delete" onClick={() => handleDelete(entry.id)}>Yes, delete</button>
                        <button className="comms-btn-cancel-delete" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
